import { Form, Formik } from "formik";
import { useState } from "react";

import { UserLocation } from "custom-models";

import { patientSchema, patientWarningSchema } from "shared/utils/caseSchema";
import { getValidationWarnings } from "shared/utils/validationUtils";

import { useAppDispatch } from "app/hooks";
import {
  updatePatient,
  updatePatientAddress,
} from "app/reducers/admin/actions/patientActions";
import { CaseState } from "app/reducers/admin/reducers/caseReducer";
import { PatientState } from "app/reducers/admin/reducers/patientReducer";
import PatientForm from "ui/CaseForms/PatientForm";

const checkAddressChanges = (
  initialAddress?: UserLocation,
  updatedAddress?: UserLocation
) => {
  return (
    updatedAddress &&
    Object.keys(updatedAddress).filter((key) => {
      const idxKey = key as keyof UserLocation;
      return initialAddress?.[idxKey] !== updatedAddress[idxKey];
    })?.length > 0
  );
};

const CasePatientForm = ({
  caseDetail,
  patient,
}: {
  caseDetail?: CaseState;
  patient?: PatientState;
}) => {
  const dispatch = useAppDispatch();

  const initialValues: PatientState = {
    first_name: patient?.first_name || "",
    last_name: patient?.last_name || "",
    middle_initial: patient?.middle_initial || "",
    ssn: patient?.ssn || "",
    date_of_birth: patient?.date_of_birth || undefined,
    sex: patient?.sex,
    medical_record_number: patient?.medical_record_number || "",

    address: {
      street: "",
      street2: "",
      city: "",
      state: "",
      zipcode: "",
      phone1: "",
      phone2: "",

      ...patient?.address,
    },
  };

  const initialAddressFilled =
    !!patient?.address &&
    Object.values(patient.address).filter((val) => val)?.length < 0;
  const [warnings, setWarnings] = useState({});

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => {
        const { address, ...rest } = values;
        patient?.id &&
          dispatch(updatePatient({ id: patient.id, requestBody: rest }));

        // Instead of simply checking if the address fields are empty, check if they are the same as before
        // this allows a user to empty non-required fields from the address when required.
        const isAddressChanged = checkAddressChanges(patient?.address, address);
        patient?.id &&
          address?.id &&
          isAddressChanged &&
          dispatch(
            updatePatientAddress({ id: address.id, requestBody: address })
          );
      }}
      validate={(values: any) => {
        patientWarningSchema
          .validate(values, {
            abortEarly: false,
          })
          .then(() => {
            setWarnings({});
          })
          .catch((err) => {
            setWarnings(getValidationWarnings(err));
          });
      }}
      validateOnBlur={false}
      validationSchema={patientSchema}
      validateOnMount={true}
    >
      {({ errors, dirty, touched, values }) => {
        return (
          <Form autoComplete="off">
            <PatientForm
              values={values}
              billingType={caseDetail?.billing_type}
              errors={errors}
              touched={touched}
              warnings={warnings}
              displaySubmit
              dirty={dirty}
              initialAddressFilled={initialAddressFilled}
              currClinic={patient?.clinic}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

export default CasePatientForm;
